import React, { useState } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DecimalKeypad } from '@/components/ui/keypads/DecimalKeypad';
import { useTableData } from '@/hooks/useTableData';
import type { InventoryAuditItem } from '@/types/inventory-audit.types';

interface InventoryAuditItemsTableProps {
  items: InventoryAuditItem[];
  auditStatus: string;
  showOnlyDiscrepancies: boolean;
  categoryFilter: string;
  reviewFilter: string;
  isCajero?: boolean;
  onQuantityChange: (itemId: string, quantity: number) => void;
  onReviewToggle: (itemId: string, isReviewed: boolean) => void;
  onConfirmQuantityChange?: (itemId: string, quantity: number) => void;
  onDiscrepancyReasonChange?: (itemId: string, reason: string) => void;
}

type SortField = 'ingredientName' | 'unit' | 'category' | 'expectedQuantity' | 'actualQuantity' | 'confirmedQuantity' | 'isReviewed' | 'discrepancy' | 'discrepancyReason';

export const InventoryAuditItemsTable: React.FC<InventoryAuditItemsTableProps> = ({
  items,
  auditStatus,
  showOnlyDiscrepancies,
  categoryFilter,
  reviewFilter,
  isCajero = false,
  onQuantityChange,
  onReviewToggle,
  onConfirmQuantityChange,
  onDiscrepancyReasonChange
}) => {
  // Estado para el keypad
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ id: string; name: string; quantity: number } | null>(null);

  const handleOpenKeypad = (item: InventoryAuditItem) => {
    setSelectedItem({
      id: item.id,
      name: item.ingredientName,
      quantity: item.actualQuantity || 0
    });
    setKeypadOpen(true);
  };

  const handleKeypadSubmit = (value: string) => {
    if (selectedItem) {
      onQuantityChange(selectedItem.id, parseFloat(value) || 0);
    }
    setKeypadOpen(false);
    setSelectedItem(null);
  };

  const handleKeypadClose = () => {
    setKeypadOpen(false);
    setSelectedItem(null);
  };
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    ingredientName: 'Producto',
    unit: 'Unidad',
    category: 'Categoría',
    expectedQuantity: 'Stock Sistema',
    actualQuantity: 'Cantidad Contada',
    confirmedQuantity: 'Cantidad a Confirmar',
    isReviewed: 'Revisado',
    discrepancy: 'Diferencia',
    discrepancyReason: 'Motivo'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (item: InventoryAuditItem, field: SortField): any => {
    switch (field) {
      case 'ingredientName':
        return item.ingredientName.toLowerCase();
      case 'unit':
        return item.unit.toLowerCase();
      case 'category':
        return item.category.toLowerCase();
      case 'expectedQuantity':
        return item.expectedQuantity;
      case 'actualQuantity':
        return item.actualQuantity || 0;
      case 'confirmedQuantity':
        return item.confirmedQuantity ?? item.actualQuantity ?? 0;
      case 'discrepancy':
        return item.discrepancy || 0;
      case 'discrepancyReason':
        return item.discrepancyReason || '';
      case 'isReviewed':
        return item.isReviewed ? 1 : 0;
      default:
        return item.ingredientName;
    }
  };

  // Filtrar items basado en los filtros seleccionados
  const getFilteredItems = () => {
    let filtered = items;

    // Filtro de discrepancias
    if (showOnlyDiscrepancies) {
      filtered = filtered.filter(item => item.discrepancy !== 0);
    }

    // Filtro de categoría
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item =>
        item.category?.trim().toLowerCase() === categoryFilter.trim().toLowerCase()
      );
    }

    // Filtro de revisión
    if ((auditStatus === 'completed' || auditStatus === 'reviewed') && reviewFilter !== 'all') {
      if (reviewFilter === 'reviewed') {
        filtered = filtered.filter(item => item.isReviewed);
      } else if (reviewFilter === 'pending') {
        filtered = filtered.filter(item => !item.isReviewed);
      }
    }

    return filtered;
  };

  // Usar el hook reutilizable
  const tableData = useTableData<InventoryAuditItem, SortField>(
    {
      data: getFilteredItems(),
      defaultSortField: 'category',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (getFilteredItems().length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">No se encontraron elementos que coincidan con los filtros</div>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="max-h-[600px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader
                  field="ingredientName"
                  onSort={tableData.handleSort}
                  getSortIcon={tableData.getSortIcon}
                  className="text-left"
                >
                  Producto
                </SortableHeader>
                <SortableHeader
                  field="unit"
                  onSort={tableData.handleSort}
                  getSortIcon={tableData.getSortIcon}
                  className="text-left"
                >
                  Unidad
                </SortableHeader>
                <SortableHeader
                  field="category"
                  onSort={tableData.handleSort}
                  getSortIcon={tableData.getSortIcon}
                  className="text-left"
                >
                  Categoría
                </SortableHeader>
                {auditStatus !== 'in_progress' && (
                  <SortableHeader
                    field="expectedQuantity"
                    onSort={tableData.handleSort}
                    getSortIcon={tableData.getSortIcon}
                    className="text-center"
                  >
                    Stock Sistema
                  </SortableHeader>
                )}
                <SortableHeader
                  field="actualQuantity"
                  onSort={tableData.handleSort}
                  getSortIcon={tableData.getSortIcon}
                  className="text-center bg-yellow-50"
                >
                  Cantidad Contada
                </SortableHeader>
                {(auditStatus === 'completed' || auditStatus === 'reviewed') && (
                  <SortableHeader
                    field="confirmedQuantity"
                    onSort={tableData.handleSort}
                    getSortIcon={tableData.getSortIcon}
                    className="text-center bg-blue-50"
                  >
                    Cantidad a Confirmar
                  </SortableHeader>
                )}
                {(auditStatus === 'completed' || auditStatus === 'reviewed') && (
                  <SortableHeader
                    field="isReviewed"
                    onSort={tableData.handleSort}
                    getSortIcon={tableData.getSortIcon}
                    className="text-center bg-green-50"
                  >
                    Revisado
                  </SortableHeader>
                )}
                {auditStatus !== 'in_progress' && (
                  <SortableHeader
                    field="discrepancy"
                    onSort={tableData.handleSort}
                    getSortIcon={tableData.getSortIcon}
                    className="text-center"
                  >
                    Diferencia
                  </SortableHeader>
                )}
                {(auditStatus === 'completed' || auditStatus === 'reviewed') && (
                  <SortableHeader
                    field="discrepancyReason"
                    onSort={tableData.handleSort}
                    getSortIcon={tableData.getSortIcon}
                    className="text-center"
                  >
                    Motivo del Error
                  </SortableHeader>
                )}
                <SortableHeader
                  field="expectedQuantity"
                  onSort={tableData.handleSort}
                  getSortIcon={tableData.getSortIcon}
                  className="text-center bg-gray-100"
                >
                  Stock Actual
                </SortableHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tableData.pagination.paginatedData.map((item) => {
                const hasQuantityEntered = item.actualQuantity !== null && item.actualQuantity !== undefined;
                const isEditable = auditStatus === 'in_progress';
                const rowClass = (isEditable && hasQuantityEntered)
                  ? "hover:bg-red-50 bg-red-50 border-l-4 border-l-red-400"
                  : "hover:bg-gray-50";

                return (
                  <tr key={item.id} className={rowClass}>
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900">{item.ingredientName}</div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">{item.unit}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">{item.category}</td>
                    {auditStatus !== 'in_progress' && (
                      <td className="px-3 py-2 text-center text-sm text-gray-900">{item.expectedQuantity}</td>
                    )}
                    <td className={`px-3 py-2 text-center ${hasQuantityEntered ? 'bg-red-100' : 'bg-yellow-50'}`}>
                      {isEditable ? (
                        <div className="flex justify-center">
                          {isCajero ? (
                            <button
                              type="button"
                              onClick={() => handleOpenKeypad(item)}
                              className="w-20 h-8 text-center text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                            >
                              {item.actualQuantity || 0}
                            </button>
                          ) : (
                            <Input
                              type="number"
                              value={item.actualQuantity}
                              onChange={(e) => onQuantityChange(item.id, parseFloat(e.target.value) || 0)}
                              className="w-20 h-8 text-center text-sm"
                              placeholder="0"
                              min="0"
                              step="0.01"
                            />
                          )}
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{item.actualQuantity}</span>
                      )}
                    </td>
                    {(auditStatus === 'completed' || auditStatus === 'reviewed') && (
                      <td className={`px-3 py-2 ${item.isReviewed ? 'bg-green-100' : 'bg-blue-50'}`}>
                        <div className="flex justify-center">
                          <Input
                            type="number"
                            value={item.confirmedQuantity !== null ? item.confirmedQuantity : item.actualQuantity}
                            onChange={(e) => onConfirmQuantityChange && onConfirmQuantityChange(item.id, parseFloat(e.target.value) || 0)}
                            className="w-20 h-8 text-center text-sm"
                            placeholder="0"
                            min="0"
                            step="0.01"
                            disabled={item.isReviewed}
                          />
                        </div>
                      </td>
                    )}
                    {(auditStatus === 'completed' || auditStatus === 'reviewed') && (
                      <td className="px-3 py-2 text-center bg-green-100">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={item.isReviewed || false}
                            onCheckedChange={(checked) => onReviewToggle(item.id, !!checked)}
                          />
                        </div>
                      </td>
                    )}
                    {auditStatus !== 'in_progress' && (
                      <td className={`px-3 py-2 text-center ${item.isReviewed ? 'bg-green-100' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-center gap-2">
                          <span className={`text-sm font-medium ${
                            item.discrepancy > 0
                              ? 'text-blue-600'
                              : item.discrepancy < 0
                                ? 'text-red-600'
                                : 'text-gray-900'}`
                          }>
                            {item.discrepancy > 0 ? '+' : ''}{item.discrepancy.toFixed(2)}
                          </span>
                          {item.discrepancy !== 0 && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                          {item.isReviewed && (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      </td>
                    )}
                    {(auditStatus === 'completed' || auditStatus === 'reviewed') && (
                      <td className={`px-3 py-2 text-center ${item.discrepancy !== 0 ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                        {item.discrepancy !== 0 ? (
                          <div className="flex justify-center">
                            {auditStatus === 'reviewed' || item.isReviewed ? (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                item.discrepancyReason
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {item.discrepancyReason || 'Sin categorizar'}
                              </span>
                            ) : (
                              <Select
                                value={item.discrepancyReason || ''}
                                onValueChange={(value) => onDiscrepancyReasonChange && onDiscrepancyReasonChange(item.id, value)}
                                disabled={false}
                              >
                                <SelectTrigger className="w-40 h-8 text-xs">
                                  <SelectValue placeholder="Seleccionar motivo" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Error de conteo">🔴 Error de conteo</SelectItem>
                                  <SelectItem value="Merma/Daño">📦 Merma/Daño</SelectItem>
                                  <SelectItem value="Robo/Pérdida">🚫 Robo/Pérdida</SelectItem>
                                  <SelectItem value="Error de sistema">⚙️ Error de sistema</SelectItem>
                                  <SelectItem value="Otro">❓ Otro</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Sin error</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-center bg-gray-50">
                      <span className="text-sm text-gray-700">{item.expectedQuantity}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />

      {/* Decimal Keypad para cajeros */}
      {isCajero && (
        <DecimalKeypad
          isOpen={keypadOpen}
          onClose={handleKeypadClose}
          onSubmit={handleKeypadSubmit}
          title={selectedItem ? `Cantidad - ${selectedItem.name}` : 'Cantidad'}
          initialValue={selectedItem?.quantity.toString() || '0'}
        />
      )}
    </div>
  );
};